import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { aboutData } from '@/mocks/about';
import { siteConfig } from '@/mocks/home';

export default function AboutPage() {
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('company_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    setFormStatus('loading');
    try {
      const payload = new URLSearchParams();
      payload.append('name', contactForm.name);
      payload.append('email', contactForm.email);
      payload.append('message', contactForm.message);
      const res = await fetch('https://readdy.ai/api/form/d9sqsvllbrt1qcgaj8fg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const text = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(text); } catch { /* ignore parse error */ }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setContactForm({ name: '', email: '', message: '' });
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteConfig.url },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: `${siteConfig.url}/about` },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors duration-200 cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium">关于我们</span>
            </nav>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">关于我们</h1>
          </div>
        </div>

        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8">
              {aboutData.brandIntro.title}
            </h2>
            {aboutData.brandIntro.paragraphs.map((p, idx) => (
              <p key={idx} className="text-foreground-700 leading-loose mb-5 text-base">
                {p}
              </p>
            ))}
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-10 text-center">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300/60 md:-translate-x-px"></div>
              <div className="flex flex-col gap-6">
                {aboutData.milestones.map((m, idx) => (
                  <div key={idx} className={`flex items-start gap-6 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className="flex-1 md:text-right hidden md:block">
                      {idx % 2 === 0 && (
                        <div className="pr-8">
                          <span className="font-heading text-xl font-bold text-primary-500">{m.year}</span>
                          <p className="text-sm text-foreground-600 mt-1">{m.event}</p>
                        </div>
                      )}
                    </div>
                    <div className="w-8 h-8 flex-shrink-0 rounded-full bg-accent-500 text-background-50 flex items-center justify-center text-xs font-bold z-10">
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <div className={`md:pl-8 ${idx % 2 === 0 ? 'md:hidden' : ''}`}>
                        <span className="font-heading text-xl font-bold text-primary-500">{m.year}</span>
                        <p className="text-sm text-foreground-600 mt-1">{m.event}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-10 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {aboutData.testimonials.map((t, idx) => (
                <div key={idx} className="bg-background-100 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-10 text-center">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div>
                <div className="flex flex-col gap-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                      <i className="ri-mail-line text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">电子邮箱</p>
                      <p className="text-sm text-foreground-600">{aboutData.contactInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                      <i className="ri-wechat-line text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">官方微信</p>
                      <p className="text-sm text-foreground-600">{aboutData.contactInfo.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                      <i className="ri-customer-service-2-line text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">客服热线</p>
                      <p className="text-sm text-foreground-600">{aboutData.contactInfo.qq}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                      <i className="ri-time-line text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">工作时间</p>
                      <p className="text-sm text-foreground-600">{aboutData.contactInfo.workingHours}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                      <i className="ri-map-pin-line text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">公司地址</p>
                      <p className="text-sm text-foreground-600">{aboutData.contactInfo.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                {formStatus === 'success' ? (
                  <div className="bg-accent-100/60 rounded-xl p-8 text-center">
                    <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-500 text-background-50 mb-4">
                      <i className="ri-check-line text-2xl"></i>
                    </div>
                    <p className="font-heading text-lg font-semibold text-foreground-950 mb-2">消息已发送</p>
                    <p className="text-sm text-foreground-600">感谢您的留言，我们会尽快回复！</p>
                  </div>
                ) : (
                  <form
                    onSubmit={handleContactSubmit}
                    className="bg-background-50 rounded-xl border border-background-200/70 p-6"
                    data-readdy-form
                    id="contact-form"
                  >
                    <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-5">发送消息</h3>

                    <div className="hp-field-wrapper">
                      <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>

                    <div className="flex flex-col gap-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                        <input
                          id="contact-name"
                          type="text"
                          name="name"
                          value={contactForm.name}
                          onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-300/60 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                        <input
                          id="contact-email"
                          type="email"
                          name="email"
                          value={contactForm.email}
                          onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-300/60 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                        <textarea
                          id="contact-message"
                          name="message"
                          value={contactForm.message}
                          onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-300/60 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200 resize-none"
                          placeholder="请输入您的留言（500字以内）"
                        ></textarea>
                        <p className="text-xs text-foreground-400 mt-1">{contactForm.message.length}/500</p>
                      </div>
                      <button
                        type="submit"
                        disabled={formStatus === 'loading'}
                        className="whitespace-nowrap cursor-pointer w-full bg-primary-500 text-background-50 py-3 rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-60 transition-all duration-200"
                      >
                        {formStatus === 'loading' ? '发送中...' : '发送消息'}
                      </button>
                      {formStatus === 'error' && (
                        <p className="text-sm text-primary-600 text-center">{formError}</p>
                      )}
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}